import { ComponentPropsWithoutRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Panel } from 'reactflow';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { AnimatePresence, motion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

const ConflictingTriggersPanel = () => {
    const [visible, setVisible] = useState(false);
    const { data: workflow } = useCurrentWorkflow();

    useEffect(() => {
        const timeout = setTimeout(() => setVisible(true), 1000);
        return () => clearTimeout(timeout);
    }, []);

    if (!workflow.has_conflicting_triggers) {
        return null;
    }

    return (
        <Panel position="top-right">
            <AnimatePresence>
                {visible && (
                    <motion.div
                        initial={{ y: -200 }}
                        exit={{ y: -200 }}
                        animate={{ y: 0 }}
                        transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
                    >
                        <Box className="relative p-4 pr-8">
                            <ConflictingTriggersAlert />
                            <Button
                                variant="transparent"
                                shape={'square'}
                                size="sm"
                                className="absolute right-1 top-1"
                                onClick={() => setVisible(false)}
                                aria-label="Close"
                            >
                                <FontAwesomeIcon icon={faTimes} size="sm" />
                            </Button>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Panel>
    );
};

interface ConflictingTriggersAlertProps extends ComponentPropsWithoutRef<'div'> {}
const ConflictingTriggersAlert = ({ className, ...rest }: ConflictingTriggersAlertProps) => {
    const { accountId } = useCurrentAccount();

    return (
        <div className={twMerge('grid max-w-96 grid-cols-[40px_1fr] gap-4', className)} {...rest}>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
            </div>
            <div className="flex flex-col gap-2">
                <p className="text-sm font-medium text-gray-700">
                    Overlaps with existing workflows
                </p>
                <p className="text-sm">
                    This workflow may trigger simultaneously with some of your existing workflows.
                </p>
                <Link to={`/accounts/${accountId}/workflows`} className="text-sm">
                    View &rarr;
                </Link>
            </div>
        </div>
    );
};

export { ConflictingTriggersPanel, ConflictingTriggersAlert };
